/**
 * useNotifications - Hook for real-time lead notifications
 *
 * Uses WebSocket for instant notifications when new leads arrive
 * from Facebook, Instagram, WhatsApp, Web, etc.
 *
 * Follows the exact pattern of useQuoteViewingNotifications.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useWebSocket } from './useWebSocket';
import { apiClient } from '@/lib/api-client';
import { conversationsApi, messagesApi } from '@/services/api/communicationsApi';
import { communicationKeys } from './useCommunications';

// Circuit breaker: stop polling after consecutive failures to prevent infinite 403 loops
const MAX_CONSECUTIVE_FAILURES = 3;

// Extended window interface for Safari AudioContext compatibility
interface WindowWithWebkitAudio extends Window {
  webkitAudioContext?: typeof AudioContext;
}

// WebSocket message types
interface NotificationWebSocketMessage {
  type: 'notification';
  notification: Notification;
}

interface UnreadNotificationsWebSocketMessage {
  type: 'unread_notifications';
  notifications: Notification[];
}

type WebSocketMessage = NotificationWebSocketMessage | UnreadNotificationsWebSocketMessage;

// Notification types from backend
export type NotificationType =
  // Lead notifications
  | 'lead_new_facebook'
  | 'lead_new_instagram'
  | 'lead_new_web'
  | 'lead_new_whatsapp'
  | 'lead_new_tiktok'
  | 'lead_new_generic'
  | 'lead_assigned'
  | 'lead_stage_changed'
  | 'lead_followup_due'
  // Message received notifications
  | 'message_received_whatsapp'
  | 'message_received_facebook'
  | 'message_received_instagram'
  | 'message_received_email'
  | 'message_received_generic'
  // Internal messaging notifications
  | 'message_internal_dm'
  | 'message_internal_group'
  // Quote notifications
  | 'quote_viewing'
  | 'quote_contact_requested'
  | 'quote_accepted'
  | 'quote_expired'
  | 'quote_expiring_soon'
  // Task notifications
  | 'task_assigned'
  // System notifications
  | 'system_alert';

export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface Notification {
  id: string | null;
  type: NotificationType;
  title: string;
  message: string;
  priority: NotificationPriority;
  lead_id: string | null;
  lead_name: string | null;
  lead_phone: string | null;
  lead_email: string | null;
  metadata: {
    source?: string;
    source_display?: string;
    project_id?: string;
    project_name?: string;
    [key: string]: string | number | boolean | null | undefined;
  };
  created_at: string | null;
  is_read: boolean;
}

interface UseNotificationsOptions {
  enabled?: boolean;
  enableSound?: boolean;
  isInOmnichannel?: boolean; // Suppress message sounds when advisor is on omnichannel page
  pollInterval?: number; // Fallback polling interval in ms (default: 10000 = 10s)
}

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  isConnected: boolean;
  error: string | null;
  dismiss: (notificationId: string) => void;
  markAsRead: (notificationId: string) => void;
  clearAll: () => void;
}

// Sound configurations per notification type
// Format: [frequencies[], volume, tempo]
const SOUND_CONFIGS: Record<string, { frequencies: number[]; volume: number; tempo: number }> = {
  // ========== LEAD NOTIFICATIONS ==========

  // High priority - Facebook Lead Ads (agudo, energético)
  lead_new_facebook: { frequencies: [880, 1100, 1320], volume: 0.22, tempo: 0.12 },

  // High priority - Instagram (brillante)
  lead_new_instagram: { frequencies: [784, 988, 1175], volume: 0.22, tempo: 0.12 },

  // High priority - WhatsApp (verde, rápido)
  lead_new_whatsapp: { frequencies: [659, 784, 880], volume: 0.22, tempo: 0.10 },

  // Normal priority - Web/Landing (suave)
  lead_new_web: { frequencies: [520, 660, 784], volume: 0.18, tempo: 0.15 },

  // Normal priority - TikTok (moderno)
  lead_new_tiktok: { frequencies: [698, 880, 1047], volume: 0.20, tempo: 0.11 },

  // Normal priority - Generic lead
  lead_new_generic: { frequencies: [523, 659, 784], volume: 0.18, tempo: 0.14 },

  // Normal priority - Lead assigned (profesional)
  lead_assigned: { frequencies: [440, 550, 660], volume: 0.16, tempo: 0.18 },

  // Low priority - Stage changed
  lead_stage_changed: { frequencies: [392, 494, 587], volume: 0.14, tempo: 0.20 },

  // Normal priority - Follow-up reminder
  lead_followup_due: { frequencies: [466, 587, 698], volume: 0.16, tempo: 0.16 },

  // ========== MESSAGE RECEIVED NOTIFICATIONS ==========

  // WhatsApp message - softer version of lead_new_whatsapp (double-tap feel)
  message_received_whatsapp: { frequencies: [587, 698], volume: 0.18, tempo: 0.10 },

  // Facebook Messenger message - Messenger-style ping
  message_received_facebook: { frequencies: [784, 880], volume: 0.18, tempo: 0.12 },

  // Instagram DM - soft Instagram chime
  message_received_instagram: { frequencies: [698, 784], volume: 0.16, tempo: 0.12 },

  // Email message - discrete, low tone
  message_received_email: { frequencies: [440, 523], volume: 0.14, tempo: 0.18 },

  // Generic message
  message_received_generic: { frequencies: [523, 587], volume: 0.16, tempo: 0.14 },

  // ========== INTERNAL MESSAGE NOTIFICATIONS ==========

  // Direct message (soft, personal tone)
  message_internal_dm: { frequencies: [523, 659], volume: 0.16, tempo: 0.14 },

  // Group message (slightly different, team feel)
  message_internal_group: { frequencies: [494, 587], volume: 0.14, tempo: 0.16 },

  // ========== QUOTE NOTIFICATIONS ==========
  // Matches sounds from useQuoteViewingNotifications for consistency

  // Quote viewing - soft bubble sound (suave, burbuja emergiendo)
  quote_viewing: { frequencies: [520, 420, 340], volume: 0.15, tempo: 0.25 },

  // Quote contact requested - urgent ring-ring (campanadas urgentes)
  quote_contact_requested: { frequencies: [880, 740, 880, 740], volume: 0.25, tempo: 0.08 },

  // Quote accepted - celebration fanfare (fanfarria ascendente)
  quote_accepted: { frequencies: [523, 659, 784, 1047], volume: 0.24, tempo: 0.15 },

  // Quote expired - descending sad tone
  quote_expired: { frequencies: [440, 392, 349], volume: 0.14, tempo: 0.22 },

  // ========== TASK NOTIFICATIONS ==========

  // Task assigned
  task_assigned: { frequencies: [494, 587, 698], volume: 0.16, tempo: 0.16 },

  // ========== DEFAULT ==========

  // Default fallback
  default: { frequencies: [523, 659, 784], volume: 0.16, tempo: 0.15 },
};

export function useNotifications({
  enabled = true,
  enableSound = true,
  isInOmnichannel = false,
  pollInterval = 10000, // 10 seconds (fallback only)
}: UseNotificationsOptions = {}): UseNotificationsReturn {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [wsConnected, setWsConnected] = useState<boolean>(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced conversation cache invalidation (prevents thundering herd on rapid messages)
  const invalidateTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Optimistically inject a message into the messages cache (instant visual update)
  const injectOptimisticMessage = useCallback((notification: Notification) => {
    const conversationId = (notification.metadata?.conversation_id as string) || null;
    const leadId = notification.lead_id;
    if (!conversationId) return;

    const optimisticMsg = {
      id: (notification.metadata?.message_id as string) || `optimistic-${Date.now()}`,
      conversation: conversationId,
      lead: leadId || '',
      direction: 'inbound' as const,
      sender_type: 'lead' as const,
      sender_name: notification.lead_name || 'Lead',
      message_type: (notification.metadata?.message_type as string) || 'text',
      content: (notification.metadata?.message_preview as string) || notification.message || '',
      media_url: '',
      media_filename: '',
      status: 'delivered',
      failure_reason: '',
      sent_at: notification.created_at || new Date().toISOString(),
      delivered_at: new Date().toISOString(),
      read_at: null,
      is_from_lead: true,
      is_from_bot: false,
      is_from_agent: false,
      has_media: false,
      ai_intent: '',
      ai_sentiment: null,
      channel_type: (notification.metadata?.channel_type as string) || null,
      metadata: {},
      created_at: notification.created_at || new Date().toISOString(),
    };

    // Optimistically update conversation card in list cache
    // (inbound message = window open + update preview/unread/timestamp)
    if (conversationId) {
      const messagePreview = (notification.metadata?.message_preview as string) || notification.message || '';
      const messageType = (notification.metadata?.message_type as string) || 'text';
      const unreadCount = notification.metadata?.unread_message_count as number | undefined;
      const messageTimestamp = notification.created_at || new Date().toISOString();

      queryClient.setQueriesData(
        { queryKey: communicationKeys.conversations() },
        (old: any) => {
          if (!old?.results) return old;
          return {
            ...old,
            results: old.results.map((conv: any) =>
              conv.id === conversationId
                ? {
                    ...conv,
                    messaging_window_open: true,
                    whatsapp_window_open: true,
                    last_message_preview: {
                      content: messagePreview,
                      sender_type: 'lead',
                      message_type: messageType,
                      sent_at: messageTimestamp,
                    },
                    last_message_at: messageTimestamp,
                    has_unread_messages: true,
                    ...(unreadCount !== undefined && unreadCount !== null
                      ? { unread_count: unreadCount }
                      : {}),
                  }
                : conv
            ),
          };
        }
      );
    }

    // Inject into conversation-level messages cache (prefix match handles dynamic limit in key)
    const msgInjector = (old: any) => {
      if (!old?.results) return old;
      if (old.results.some((m: any) => m.id === optimisticMsg.id)) return old;
      return { ...old, count: (old.count || 0) + 1, results: [...old.results, optimisticMsg] };
    };

    queryClient.setQueriesData(
      { queryKey: communicationKeys.conversationMessages(conversationId) },
      msgInjector
    );

    // Inject into lead-level messages cache (for grouped conversations)
    if (leadId) {
      queryClient.setQueriesData(
        { queryKey: [...communicationKeys.messages(), 'by-lead'] },
        msgInjector
      );
    }
  }, [queryClient]);

  const invalidateConversations = useCallback((conversationId?: string, leadId?: string) => {
    if (invalidateTimerRef.current) clearTimeout(invalidateTimerRef.current);
    invalidateTimerRef.current = setTimeout(() => {
      // Invalidate conversation list (cards update) — use conversations() prefix
      // to match all list variants regardless of filter params
      queryClient.invalidateQueries({ queryKey: communicationKeys.conversations() });

      if (conversationId) {
        // Invalidate conversation detail (window state refresh)
        queryClient.invalidateQueries({
          queryKey: communicationKeys.conversationDetail(conversationId),
        });

        // Invalidate conversation-level messages (prefix match handles dynamic limit in key)
        const convKeyPrefix = communicationKeys.conversationMessages(conversationId);
        const existingConvQueries = queryClient.getQueriesData({ queryKey: convKeyPrefix });
        if (existingConvQueries.length > 0) {
          queryClient.invalidateQueries({ queryKey: convKeyPrefix });
        } else {
          queryClient.prefetchQuery({
            queryKey: [...convKeyPrefix, { limit: 50 }],
            queryFn: () => conversationsApi.getMessages(conversationId, { limit: 50 }),
            staleTime: 10000,
          });
        }
      }

      // Also invalidate lead-level messages (for grouped conversations)
      if (leadId) {
        const leadKeyPrefix = [...communicationKeys.messages(), 'by-lead', leadId];
        const existingLeadQueries = queryClient.getQueriesData({ queryKey: leadKeyPrefix });
        if (existingLeadQueries.length > 0) {
          queryClient.invalidateQueries({ queryKey: leadKeyPrefix });
        } else {
          queryClient.prefetchQuery({
            queryKey: [...leadKeyPrefix, { limit: 50 }],
            queryFn: () => messagesApi.byLead(leadId, { limit: 50, offset: 0 }),
            staleTime: 10000,
          });
        }
      }
    }, 200); // 200ms debounce (backend rate-limits at 5s minimum)
  }, [queryClient]);

  // Audio context for sound generation
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioUnlockedRef = useRef<boolean>(false);
  const isFirstMessageRef = useRef<boolean>(true);

  // Initialize and unlock audio on first user interaction
  useEffect(() => {
    const unlockAudio = async () => {
      if (audioUnlockedRef.current) return;

      try {
        if (!audioContextRef.current) {
          const AudioContextClass = window.AudioContext || (window as WindowWithWebkitAudio).webkitAudioContext;
          if (AudioContextClass) {
            audioContextRef.current = new AudioContextClass();
          }
        }

        const audioContext = audioContextRef.current;
        if (!audioContext) return;

        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        // Play a silent sound to unlock audio
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        gainNode.gain.value = 0;
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.001);

        audioUnlockedRef.current = true;
      } catch {
        // Silent fail
      }
    };

    // Try to unlock immediately
    unlockAudio();

    // Also unlock on user interaction
    const events = ['click', 'touchstart', 'keydown', 'mousemove', 'scroll'];
    events.forEach(event => {
      document.addEventListener(event, unlockAudio, { once: true, passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, unlockAudio);
      });
    };
  }, []);

  // Play notification sound based on type
  const playNotificationSound = useCallback(async (notificationType: NotificationType) => {
    if (!enableSound) return;

    try {
      let audioContext = audioContextRef.current;
      if (!audioContext) {
        const AudioContextClass = window.AudioContext || (window as WindowWithWebkitAudio).webkitAudioContext;
        if (!AudioContextClass) return;
        audioContext = new AudioContextClass();
        audioContextRef.current = audioContext;
      }

      if (audioContext.state === 'suspended') {
        try {
          await audioContext.resume();
        } catch {
          return;
        }
      }

      // Get sound config for this notification type
      const config = SOUND_CONFIGS[notificationType] || SOUND_CONFIGS.default;
      const { frequencies, volume, tempo } = config;

      // Play ascending tones
      const now = audioContext.currentTime;

      frequencies.forEach((frequency, index) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';

        const startTime = now + (index * tempo);
        const duration = tempo * 1.5;

        // Envelope: attack + decay
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      });
    } catch {
      // Silent fail
    }
  }, [enableSound]);

  // Track previous quote IDs to detect truly NEW quotes (for sound)
  const previousQuoteIdsRef = useRef<Set<string>>(new Set());

  // Helper: Check if notification is a quote type
  const isQuoteNotificationType = (type: string) => {
    return type.startsWith('quote_');
  };

  // Helper: Check if notification is any message type (external or internal)
  const isMessageNotificationType = (type: string) => {
    return type.startsWith('message_received_') || type.startsWith('message_internal_');
  };

  // Helper: Get quote_id from notification metadata
  const getQuoteIdFromNotification = (notification: Notification): string | null => {
    return notification.metadata?.quote_id || null;
  };

  // Helper: Get conversation_id from notification metadata
  const getConversationIdFromNotification = (notification: Notification): string | null => {
    return (notification.metadata?.conversation_id as string) || null;
  };

  // Deduplicate message notifications by conversation_id (keep most recent)
  // Applies to both external (message_received_*) and internal (message_internal_*)
  const deduplicateMessageNotifications = useCallback((notifs: Notification[]): Notification[] => {
    const seenConversations = new Set<string>();
    return notifs.filter(n => {
      if (isMessageNotificationType(n.type)) {
        const convId = (n.metadata?.conversation_id as string) || null;
        if (convId) {
          if (seenConversations.has(convId)) return false;
          seenConversations.add(convId);
        }
      }
      return true;
    });
  }, []);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((data: WebSocketMessage) => {
    // New notification received
    if (data.type === 'notification') {
      const notification = data.notification;

      // Generate client-side ID if notification doesn't have one (e.g., quote_viewing with persist=false)
      const notificationWithId: Notification = {
        ...notification,
        id: notification.id || `client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      };

      const isQuoteNotification = isQuoteNotificationType(notification.type);
      const quoteId = getQuoteIdFromNotification(notification);
      const isMessageNotification = isMessageNotificationType(notification.type);
      const conversationId = getConversationIdFromNotification(notification);

      // Update notifications list
      setNotifications(prev => {
        // For quote notifications: REPLACE existing notification for the same quote
        // This ensures we show updated view counts instead of stacking notifications
        if (isQuoteNotification && quoteId) {
          const existingIndex = prev.findIndex(n =>
            isQuoteNotificationType(n.type) && getQuoteIdFromNotification(n) === quoteId
          );

          if (existingIndex !== -1) {
            // REPLACE existing notification with updated data
            const updated = [...prev];
            updated[existingIndex] = notificationWithId;
            return updated;
          }
        }

        // For message notifications: REPLACE existing notification for the same conversation
        // This avoids stacking when a lead sends multiple messages quickly
        if (isMessageNotification && conversationId) {
          const existingIndex = prev.findIndex(n =>
            isMessageNotificationType(n.type) && getConversationIdFromNotification(n) === conversationId
          );

          if (existingIndex !== -1) {
            const updated = [...prev];
            updated[existingIndex] = notificationWithId;
            return updated;
          }
        }

        // For non-quote notifications or new quotes: avoid duplicates by server ID
        if (notification.id && prev.some(n => n.id === notification.id)) {
          return prev;
        }

        // ADD new notification at the beginning
        return [notificationWithId, ...prev].slice(0, 50);
      });

      // Real-time message update: optimistic insert + debounced invalidation
      if (isMessageNotification) {
        // 1. Instant: inject message into cache (0ms delay)
        injectOptimisticMessage(notification);
        // 2. Background: invalidate to get authoritative data (200ms debounced)
        invalidateConversations(conversationId || undefined, notification.lead_id || undefined);
      }

      // Play sound logic:
      // - Skip first message (reconnect)
      // - For quote_viewing: only play if this is a NEW quote (not an update)
      // - For quote_contact_requested / quote_accepted: always play (high priority)
      // - For message_received with is_update=true: skip sound (Tier 2 light update)
      // - For message_received when on omnichannel: skip sound (cards are enough)
      // - For lead notifications: always play
      if (!isFirstMessageRef.current && enableSound) {
        let shouldPlaySound = true;

        if (notification.type === 'quote_viewing' && quoteId) {
          // Only play sound for NEW quotes, not updates
          if (previousQuoteIdsRef.current.has(quoteId)) {
            shouldPlaySound = false;
          } else {
            previousQuoteIdsRef.current.add(quoteId);
          }
        }

        if (shouldPlaySound) {
          playNotificationSound(notificationWithId.type);
        }
      }

      // Invalidate related queries so tables refresh in real-time
      if (notification.type === 'quote_expired' || notification.type === 'quote_accepted' || notification.type === 'quote_contact_requested') {
        queryClient.invalidateQueries({ queryKey: ['leads-paginated'] });
        queryClient.invalidateQueries({ queryKey: ['quotes-paginated'] });
      }

    }

    // Unread notifications on connect
    if (data.type === 'unread_notifications') {
      setNotifications(deduplicateMessageNotifications(data.notifications || []));
      isFirstMessageRef.current = false;

      // Initialize previous quote IDs from unread notifications
      const quoteIds = new Set<string>();
      (data.notifications || []).forEach((n: Notification) => {
        const qid = getQuoteIdFromNotification(n);
        if (qid) quoteIds.add(qid);
      });
      previousQuoteIdsRef.current = quoteIds;
    }
  }, [enableSound, isInOmnichannel, invalidateConversations, injectOptimisticMessage, playNotificationSound, deduplicateMessageNotifications, queryClient]);

  // WebSocket callbacks
  const handleWsConnect = useCallback(() => {
    setWsConnected(true);
    setError(null);
  }, []);

  const handleWsDisconnect = useCallback(() => {
    setWsConnected(false);
  }, []);

  const handleWsError = useCallback(() => {
    setError('WebSocket connection error');
  }, []);

  // WebSocket connection - all authenticated users receive real-time notifications
  const shouldConnect = enabled && isAuthenticated && !!user;

  const { sendMessage } = useWebSocket('/ws/notifications/', {
    enabled: shouldConnect,
    onMessage: handleWebSocketMessage,
    onConnect: handleWsConnect,
    onDisconnect: handleWsDisconnect,
    onError: handleWsError,
  });

  // Dismiss notification (local + server via WS + REST fallback)
  const dismiss = useCallback(async (notificationId: string) => {
    // Remove from local state immediately
    setNotifications(prev => prev.filter(n => n.id !== notificationId));

    // Skip server calls for client-generated IDs (non-persisted notifications)
    if (notificationId.startsWith('client-')) return;

    // Try WebSocket first
    if (sendMessage) {
      sendMessage({ type: 'dismiss', notification_id: notificationId });
    }

    // Also call REST API to ensure dismiss is persisted server-side
    try {
      apiClient.post(`/notifications/${notificationId}/dismiss/`);
    } catch {
      // Silent fail - WS dismiss is the primary mechanism
    }
  }, [sendMessage]);

  // Mark as read (local + server via WS + REST fallback)
  const markAsRead = useCallback(async (notificationId: string) => {
    // Update local state
    setNotifications(prev =>
      prev.map(n =>
        n.id === notificationId ? { ...n, is_read: true } : n
      )
    );

    // Skip server calls for client-generated IDs
    if (notificationId.startsWith('client-')) return;

    // Try WebSocket first
    if (sendMessage) {
      sendMessage({ type: 'mark_read', notification_id: notificationId });
    }

    // Also call REST API to ensure it's persisted
    try {
      apiClient.post(`/notifications/${notificationId}/read/`);
    } catch {
      // Silent fail
    }
  }, [sendMessage]);

  // Clear all notifications (local only)
  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // Fetch notifications via REST API (fallback when WebSocket fails)
  // Circuit breaker: stops polling after MAX_CONSECUTIVE_FAILURES to prevent infinite 403 loops
  const consecutiveFailuresRef = useRef<number>(0);

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated || !user) return;

    // Circuit breaker: stop polling if too many consecutive failures
    if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
      return;
    }

    try {
      const data = await apiClient.get<{ notifications: Notification[] }>('/notifications/', {
        params: { limit: 20 },
      });
      const fetchedNotifications = data.notifications || [];

      // Reset circuit breaker on success
      consecutiveFailuresRef.current = 0;

      // Update notifications state — deduplicate message_received by conversation_id
      const deduped = deduplicateMessageNotifications(fetchedNotifications);
      setNotifications(deduped);

      // Invalidate conversations if any message notifications exist (polling fallback for WS)
      const hasMessageNotifs = deduped.some((n: Notification) =>
        (n.type?.startsWith('message_received_') || n.type?.startsWith('message_internal_')) && !n.is_read
      );
      if (hasMessageNotifs) {
        queryClient.invalidateQueries({ queryKey: communicationKeys.conversations() });
      }

      // Mark first fetch as done (for sound logic)
      if (isFirstMessageRef.current) {
        isFirstMessageRef.current = false;

        // Initialize previous quote IDs from fetched notifications
        const quoteIds = new Set<string>();
        fetchedNotifications.forEach((n: Notification) => {
          const qid = getQuoteIdFromNotification(n);
          if (qid) quoteIds.add(qid);
        });
        previousQuoteIdsRef.current = quoteIds;
      }
    } catch {
      consecutiveFailuresRef.current += 1;
      // apiClient handles 401→login redirect and 403→token refresh automatically
      // If we hit the circuit breaker, polling stops until WS reconnects
    }
  }, [isAuthenticated, user, deduplicateMessageNotifications, queryClient]);

  // Setup polling (fallback only if WebSocket is disconnected)
  useEffect(() => {
    // Only poll if WebSocket is not connected but we should be enabled
    const shouldPoll = !wsConnected && enabled && isAuthenticated && user;

    if (!shouldPoll) {
      // Clear polling if not needed
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      // Reset circuit breaker when WS reconnects so polling can resume if it drops again
      if (wsConnected) {
        consecutiveFailuresRef.current = 0;
      }
      return;
    }

    // Reset circuit breaker when starting fresh polling cycle
    consecutiveFailuresRef.current = 0;

    // Initial fetch
    fetchNotifications();

    // Setup polling interval
    pollIntervalRef.current = setInterval(fetchNotifications, pollInterval);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [wsConnected, enabled, isAuthenticated, user, pollInterval, fetchNotifications]);

  // Periodically check for expired quotes (triggers backend auto-expire + notification)
  const expirationCheckRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    const shouldCheck = enabled && isAuthenticated && !!user && user.role === 'sales_advisor';
    if (!shouldCheck) {
      if (expirationCheckRef.current) {
        clearInterval(expirationCheckRef.current);
        expirationCheckRef.current = null;
      }
      return;
    }

    const checkExpirations = async () => {
      try {
        await apiClient.post('/quotes/check-expirations/');
      } catch {
        // Silent fail — apiClient handles token refresh automatically
      }
    };

    // Check immediately and then every 60 seconds
    checkExpirations();
    expirationCheckRef.current = setInterval(checkExpirations, 60000);

    return () => {
      if (expirationCheckRef.current) {
        clearInterval(expirationCheckRef.current);
        expirationCheckRef.current = null;
      }
    };
  }, [enabled, isAuthenticated, user]);

  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.is_read).length;

  return {
    notifications,
    unreadCount,
    isConnected: wsConnected,
    error,
    dismiss,
    markAsRead,
    clearAll,
  };
}
